import { useNavigate } from 'react-router-dom';
import { FileText, BarChart3 } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { CreditLevelBadge } from '@/features/credit/components/CreditLevelBadge';
import { CreditLevel } from '@/types/credit.types';
import { CopyableId } from '@/components/ui/CopyableId';
import { ROUTES } from '@/constants';
import { formatCurrency } from '@/utils/formatters';
import type { CreditProfile } from '@/types/scoring.types';
import { IncomeSourcesList } from './IncomeSourcesList';

interface CandidatoDetailPanelProps {
  profile: CreditProfile | null;
}

const FREQUENCY_COLORS: Record<string, string> = {
  WEEKLY: 'text-sky-400',
  BIWEEKLY: 'text-violet-400',
  MONTHLY: 'text-teal-400',
  DAILY: 'text-indigo-400',
  IRREGULAR: 'text-slate-400',
};

export function CandidatoDetailPanel({ profile }: CandidatoDetailPanelProps) {
  const navigate = useNavigate();

  if (!profile) {
    return (
      <p className="py-12 text-center text-sm text-muted-foreground">
        Selecciona un candidato de la lista para ver el detalle.
      </p>
    );
  }

  const expedienteUrl = `${ROUTES.EXPEDIENTE_DETAIL.replace(':directoryId', profile.directory_id)}?from=candidatos`;
  const profileUrl = `${ROUTES.FINANCIAL_PROFILE.replace(':directoryId', profile.directory_id)}?from=candidatos`;
  const freqColor = FREQUENCY_COLORS[profile.dominant_frequency] ?? 'text-muted-foreground';

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <h3 className="truncate text-lg font-semibold text-foreground">
          {profile.full_name || 'Pendiente'}
        </h3>
        <div className="flex shrink-0 items-center gap-1.5">
          <IconAction
            icon={FileText}
            label="Ver Expediente"
            onClick={() => navigate(expedienteUrl, { state: { displayName: profile.full_name, phoneNumber: profile.phone_number } })}
          />
          <IconAction
            icon={BarChart3}
            label="Ver Perfil Financiero"
            onClick={() => navigate(profileUrl, { state: { displayName: profile.full_name, phoneNumber: profile.phone_number } })}
          />
        </div>
      </div>

      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        <span>{profile.phone_number || 'Sin telefono'}</span>
        <span>·</span>
        <CopyableId label="DIR" value={profile.directory_id} />
      </div>

      <div className="mt-2.5 flex items-center gap-1.5">
        {Object.values(CreditLevel).includes(profile.credit_level as CreditLevel) ? (
          <CreditLevelBadge level={profile.credit_level as CreditLevel} size="sm" />
        ) : (
          <Badge variant="default">{profile.credit_level}</Badge>
        )}
        {(profile as { credit_level_source?: string }).credit_level_source === 'WHITELIST' && (
          <span title="Nivel forzado desde Whitelist Oro (pruebas productivas), no proviene del score real">
            <Badge variant="warning">Whitelist</Badge>
          </span>
        )}
        <Badge variant={profile.is_eligible === 'YES' ? 'success' : 'danger'}>
          {profile.is_eligible === 'YES' ? 'ELEGIBLE' : 'NO ELEGIBLE'}
        </Badge>
      </div>

      {profile.is_eligible === 'NO' && profile.ineligibility_reason && (
        <div className="mt-3 rounded-md border-l-[3px] border-red-500 bg-red-500/10 px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Razon de inelegibilidad
          </p>
          <p className="mt-0.5 text-xs text-red-400">{profile.ineligibility_reason}</p>
        </div>
      )}

      <dl className="mt-3 space-y-1">
        <DetailRow label="Score" value={String(profile.score_total ?? 0)} />
        <DetailRow label="Ingreso mensual" value={formatCurrency(profile.monthly_income_mxn, 'MXN', 'es-MX')} />
        <DetailRow
          label="Credito maximo"
          value={profile.is_eligible === 'YES' ? formatCurrency(profile.max_credit_amount_mxn, 'MXN', 'es-MX') : '—'}
          valueClassName={profile.is_eligible === 'YES' ? 'text-green-600 font-medium' : ''}
        />
        <DetailRow
          label="Frecuencia dominante"
          value={profile.dominant_frequency}
          valueClassName={`${freqColor} font-medium text-[11px]`}
        />
        <DetailRow label="Fuentes detectadas" value={String(profile.total_income_sources)} />
        <DetailRow label="Dias desde ultimo deposito" value={String(profile.days_since_last_deposit)} />
        <DetailRow label="Fecha de calculo" value={profile.calculation_date} />
      </dl>

      <div className="mt-4">
        <h4 className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Fuentes de Ingreso
        </h4>
        <IncomeSourcesList directoryId={profile.directory_id} />
      </div>
    </div>
  );
}

function IconAction({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof FileText;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card text-muted-foreground transition-colors hover:border-primary hover:bg-primary/10 hover:text-primary"
    >
      <Icon size={14} />
    </button>
  );
}

function DetailRow({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="flex justify-between border-b border-border/50 py-1 text-xs">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`tabular-nums text-foreground ${valueClassName ?? ''}`}>{value}</dd>
    </div>
  );
}
